import type { MailMessageSummary } from "@/lib/types/mail";

const STORAGE_KEY = "mail_inbox_headers_session_v1";
const MAX_SESSION_ENTRIES = 64;
/** Au-delà, on ignore l’hydratation (DD02 rattrapera). */
export const INBOX_HEADERS_SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type InboxHeadersSessionEntry = {
  accountId: number;
  folder: string;
  messages: MailMessageSummary[];
  total: number;
  offset: number;
  savedAt: number;
};

function readAll(): InboxHeadersSessionEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as InboxHeadersSessionEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(entries: InboxHeadersSessionEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_SESSION_ENTRIES)));
  } catch {
    /* quota */
  }
}

export function readHeadersSessionEntriesForAccounts(
  accountIds: number[]
): InboxHeadersSessionEntry[] {
  const idSet = new Set(accountIds);
  return readAll().filter((e) => idSet.has(e.accountId));
}

export function readInboxHeadersSessionCache(
  accountId: number,
  folder = "INBOX"
): InboxHeadersSessionEntry | null {
  const match = readAll().find((e) => e.accountId === accountId && e.folder === folder);
  if (!match) return null;
  if (match.messages.length === 0 && match.total > 0) return null;
  if (Date.now() - match.savedAt > INBOX_HEADERS_SESSION_MAX_AGE_MS) return null;
  return match;
}

export function persistInboxHeadersSession(
  accountId: number,
  folder: string,
  messages: MailMessageSummary[],
  total: number,
  offset: number
): void {
  if (messages.length === 0 && total > 0) return;

  const entry: InboxHeadersSessionEntry = {
    accountId,
    folder,
    messages,
    total,
    offset,
    savedAt: Date.now(),
  };
  const rest = readAll().filter((e) => !(e.accountId === accountId && e.folder === folder));
  writeAll([entry, ...rest]);
}

export function clearInboxHeadersSessionCache(accountId?: number): void {
  if (accountId == null) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return;
  }
  writeAll(readAll().filter((e) => e.accountId !== accountId));
}
